import React, { useCallback, useEffect, useState } from 'react';
import ContentLayout from '../../layout/ContentLayout';
import { useAuth } from '../../context/useAuth';
import GenericTable from '../../components/tables/GenericTable';
import Pagination from '../../components/layout/Pagination';
import Spinner from '../../components/elements/Spinner/Spinner';
import { formatPriceWithCurrency } from '../../utils/formatPrice';
import agentService from '../../api/agent-api';
import { Link } from 'react-router-dom';
import dayjs from 'dayjs';
import { Button, ConfigProvider, DatePicker, Dropdown } from 'antd';
import { usePermissions } from '../../hooks/usePermissions';
import {
  PAYOUT_STATUSES,
  formatPayoutStatusAsPill,
} from '../../utils/formatPayoutStatus';
import '../../styles/clinic-medic.scss';

const { RangePicker } = DatePicker;

const payoutStatusOptions = [
  { label: 'Na čekanju', value: 'pending' },
  { label: 'Realizovano', value: 'paid' },
];

const summaryCardStyle = {
  border: '1px solid #E5E7EB',
  borderRadius: 12,
  padding: '20px 24px',
  minWidth: 200,
};

const columns = [
  { key: 'monthKey', label: 'Mesec' },
  {
    key: 'totalAmount',
    label: 'Ukupna provizija',
    render: (val) => formatPriceWithCurrency(val || 0),
  },
  {
    key: 'paidAmount',
    label: 'Realizovano',
    render: (val) => formatPriceWithCurrency(val || 0),
  },
  {
    key: 'pendingAmount',
    label: 'Na čekanju',
    render: (val) => formatPriceWithCurrency(val || 0),
  },
  { key: 'count', label: 'Broj stavki' },
];

const orderColumns = (canDo) => [
  {
    key: 'customer',
    label: 'Kupac',
    render: (_, row) => {
      const name = row?.customer?.fullName || '-';
      return row?.customerId && canDo('users:read') ? (
        <Link
          to={`/korisnik/detalji/${row.customerId}`}
          state={{ firstName: name, from: '/agent-home' }}
        >
          {name}
        </Link>
      ) : (
        name
      );
    },
  },
  { key: 'orderNumber', label: 'Broj porudžbine' },
  {
    key: 'commissionBase',
    label: 'Osnovica za proviziju',
    render: (val) => formatPriceWithCurrency(val || 0),
  },
  {
    key: 'commissionAgent',
    label: 'Provizija',
    render: (val, row) =>
      row.commissionEligible === false ? (
        <span style={{ color: '#6B7280' }}>Bez preporuke mentora</span>
      ) : (
        formatPriceWithCurrency(val || 0)
      ),
  },
  {
    key: 'percent',
    label: 'Procenat provizije',
    render: (val) => (val != null ? `${val}%` : '-'),
  },
  {
    key: 'payoutStatus',
    label: 'Status provizije',
    render: (val, row) => formatPayoutStatusAsPill(row),
  },
  {
    key: 'createdAt',
    label: 'Datum kupovine',
    render: (value) => {
      const date_render = dayjs(value).isValid()
        ? dayjs(value).format('MM.DD.YYYY')
        : null;
      return date_render ? `${date_render}.` : '-';
    },
  },
];

export default function AgentHome() {
  const { user } = useAuth();
  const { can: canDo } = usePermissions();
  const [agent, setAgent] = useState(null);
  const [ledgerItems, setLedgerItems] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(true);

  const [orderItems, setOrderItems] = useState([]);
  const [ordersPage, setOrdersPage] = useState(1);
  const [ordersTotalPages, setOrdersTotalPages] = useState(1);
  const [orderFilter, setOrderFilter] = useState({
    createdFrom: null,
    createdTo: null,
    payoutStatus: null,
  });

  const handleDateRangeChange = (dates) => {
    setOrderFilter((prev) => ({
      ...prev,
      createdFrom: dates?.[0] ? dates[0].startOf('day').toISOString() : null,
      createdTo: dates?.[1] ? dates[1].endOf('day').toISOString() : null,
    }));
    setOrdersPage(1);
  };

  const activePayoutStatusLabel = orderFilter.payoutStatus
    ? payoutStatusOptions.find((o) => o.value === orderFilter.payoutStatus)
        ?.label
    : null;

  const handlePayoutStatusSelect = (value) => {
    setOrderFilter((prev) => ({ ...prev, payoutStatus: value }));
    setOrdersPage(1);
  };

  const handleClearPayoutStatus = (e) => {
    e.stopPropagation();
    setOrderFilter((prev) => ({ ...prev, payoutStatus: null }));
    setOrdersPage(1);
  };

  const handleExportOrders = async () => {
    const { data, success, message } = await agentService.exportOrders(
      user._id
    );
    if (!success) {
      console.error(message || 'Error while exporting the table.');
      return;
    }
    const blob = new Blob([data], { type: 'text/csv' });
    const file = new File([blob], 'agent-orders.csv', {
      type: blob.type,
      lastModified: new Date(),
    });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(file);
    link.download = file.name;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(link.href);
  };

  const loadOrders = useCallback(async () => {
    if (!user?._id) return;
    const { data, success, message } = await agentService.getOrders(user._id, {
      page: ordersPage,
      limit: 10,
      createdFrom: orderFilter.createdFrom || undefined,
      createdTo: orderFilter.createdTo || undefined,
      payoutStatus: orderFilter.payoutStatus || undefined,
    });
    if (success) {
      setOrderItems(data?.items || []);
      setOrdersTotalPages(data?.totalPages || 1);
    } else {
      console.error(message || 'Error while fetching agent orders');
    }
  }, [
    user?._id,
    ordersPage,
    orderFilter.createdFrom,
    orderFilter.createdTo,
    orderFilter.payoutStatus,
  ]);

  const loadData = useCallback(async () => {
    if (!user?._id) return;
    setLoading(true);
    const [detailsRes, ledgerRes] = await Promise.all([
      agentService.getDetails(user._id),
      agentService.getCommissionLedger(user._id, currentPage, 10),
    ]);
    setLoading(false);
    if (detailsRes.success) setAgent(detailsRes.data);
    if (ledgerRes.success) {
      setLedgerItems(ledgerRes.data?.items || []);
      setTotalPages(ledgerRes.data?.totalPages || 1);
    }
  }, [user?._id, currentPage]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  useEffect(() => {
    loadOrders();
  }, [loadOrders]);

  if (loading && !agent) {
    return (
      <ContentLayout>
        <Spinner size="lg" />
      </ContentLayout>
    );
  }

  const totalCommission = (agent?.commissionLedgerSummary || []).reduce(
    (sum, row) => sum + (row.totalAmount || 0),
    0
  );

  return (
    <ContentLayout>
      <p className="title" style={{ marginBottom: 16 }}>
        Moj nalog
      </p>
      <div
        style={{ display: 'flex', gap: 16, flexWrap: 'wrap', marginBottom: 24 }}
      >
        <div style={summaryCardStyle}>
          <div style={{ color: '#6B7280', fontSize: 13, marginBottom: 6 }}>
            Referral kod
          </div>
          <div style={{ fontSize: 22, fontWeight: 600 }}>
            {agent?.referralCode || '-'}
          </div>
        </div>
        <div style={summaryCardStyle}>
          <div style={{ color: '#6B7280', fontSize: 13, marginBottom: 6 }}>
            Povezani mentor
          </div>
          <div style={{ fontSize: 22, fontWeight: 600 }}>
            {agent?.linkedMentor
              ? `${agent.linkedMentor.firstName} ${agent.linkedMentor.lastName}`
              : '-'}
          </div>
        </div>
        <div style={summaryCardStyle}>
          <div style={{ color: '#6B7280', fontSize: 13, marginBottom: 6 }}>
            Ukupan broj kupovina
          </div>
          <div style={{ fontSize: 22, fontWeight: 600 }}>
            {agent?.ordersPriceSummary?.count ?? 0}
          </div>
        </div>
        <div style={summaryCardStyle}>
          <div style={{ color: '#6B7280', fontSize: 13, marginBottom: 6 }}>
            Ukupna vrednost kupovina
          </div>
          <div style={{ fontSize: 22, fontWeight: 600 }}>
            {formatPriceWithCurrency(agent?.ordersPriceSummary?.total || 0)}
          </div>
        </div>
        <div style={summaryCardStyle}>
          <div style={{ color: '#6B7280', fontSize: 13, marginBottom: 6 }}>
            Ukupna provizija
          </div>
          <div style={{ fontSize: 22, fontWeight: 600 }}>
            {formatPriceWithCurrency(totalCommission)}
          </div>
        </div>
      </div>

      <div className="clinic-medic-table-actions">
        <div className="label">
          <span>Moje porudžbine</span>
        </div>

        <div className="actions">
          <ConfigProvider
            theme={{
              token: {
                colorPrimary: '#005844',
                controlOutline: 'rgba(0, 88, 68, 0.1)',
                colorPrimaryHover: '#004636',
              },
              components: {
                DatePicker: {
                  activeBorderColor: '#005844',
                  hoverBorderColor: '#005844',
                },
              },
            }}
          >
            <RangePicker
              format="DD.MM.YYYY"
              onChange={handleDateRangeChange}
              placeholder={['Datum od', 'Datum do']}
              allowClear
            />
          </ConfigProvider>
          <Dropdown
            menu={{
              items: payoutStatusOptions.map((option) => ({
                key: option.value,
                label: option.label,
                onClick: () => handlePayoutStatusSelect(option.value),
              })),
            }}
            trigger={['click']}
            placement="bottomLeft"
            className="btn-filter-container"
          >
            <Button
              className={`btn-tertiary btn-filter ${orderFilter.payoutStatus ? 'active' : ''}`}
            >
              {activePayoutStatusLabel
                ? `Provizija: ${activePayoutStatusLabel}`
                : 'Status provizije'}
              {orderFilter.payoutStatus && (
                <img
                  src="/assets/icons/x-gray.svg"
                  title="Ukloni filter"
                  style={{
                    width: '10px',
                    height: '10px',
                    marginLeft: '5px',
                    cursor: 'pointer',
                  }}
                  alt="Ukloni filter"
                  onClick={handleClearPayoutStatus}
                />
              )}
            </Button>
          </Dropdown>
          <Button
            style={{ maxHeight: '38px', padding: '8px 16px' }}
            variant="primary"
            className="btn-primary"
            htmlType="button"
            type="button"
            onClick={handleExportOrders}
          >
            Export CSV file
          </Button>
        </div>
      </div>

      <div
        className="payoutLegend"
        style={{ justifyContent: 'flex-end', marginBottom: '12px' }}
      >
        {PAYOUT_STATUSES.map((s) => (
          <span key={s.key} className="legendItem">
            <span className={`payoutDot ${s.key}`} />
            {s.label}
          </span>
        ))}
      </div>

      <GenericTable
        data={orderItems}
        columns={orderColumns(canDo)}
        emptyState={{
          icon: '/assets/icons/no-ads.svg',
          title: 'Nema porudžbina',
        }}
        rowClassName={(row) => (row.payoutStatus === 'paid' ? 'row--paid' : '')}
      />
      {ordersTotalPages > 1 && (
        <Pagination
          currentPage={ordersPage}
          totalPages={ordersTotalPages}
          onPageChange={setOrdersPage}
        />
      )}

      {/* Monthly commission ledger table disabled (2026-10-13), per request — the
          "Moje porudžbine" per-order table above plus the "Ukupna provizija" summary
          card already cover this, so the redundant monthly breakdown was hidden.
      <p className="title" style={{ marginTop: 32, marginBottom: 16 }}>
        Moja provizija
      </p>
      <GenericTable
        data={ledgerItems}
        columns={columns}
        emptyState={{
          icon: '/assets/icons/no-ads.svg',
          title: 'Nema podataka o proviziji',
        }}
      />
      {totalPages > 1 && (
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={setCurrentPage}
        />
      )}
      */}
    </ContentLayout>
  );
}
